import { useMemo, useState } from 'react';
import { Bell, MapPinned, Settings2, ShieldCheck } from 'lucide-react';
import { AdminSheet } from './components/AdminSheet';
import { FamilyTools } from './components/FamilyTools';
import { GrandpaCallCounter } from './components/GrandpaCallCounter';
import { LocationCard } from './components/LocationCard';
import { MeetupBanner } from './components/MeetupBanner';
import { Onboarding } from './components/Onboarding';
import type { FamilyLocation, FamilyProfile, GroupMeetup } from './types';

const familyId = 'demo-family';
const initialLocations: FamilyLocation[] = [
  ['camera', 'Camera', '🛏️', 'room', 'in camera'],
  ['piscina', 'Piscina', '🏊', 'pool', 'in piscina'],
  ['spiaggia', 'Spiaggia', '🏖️', 'beach', 'in spiaggia'],
  ['area-relax', 'Area relax', '🧘', 'relax', 'nell’area relax'],
  ['ristorante', 'Ristorante', '🍽️', 'restaurant', 'al ristorante'],
  ['in-giro', 'In giro', '🗺️', 'around', 'in giro'],
  ['palestra', 'Palestra', '🏋🏻', 'gym', 'in palestra'],
].map(([slug, name, emoji, kind, phrase], index) => ({
  id: slug,
  family_id: familyId,
  slug,
  name,
  emoji,
  kind: kind as FamilyLocation['kind'],
  notification_phrase: phrase,
  sort_order: (index + 1) * 10,
  is_active: true,
  created_at: new Date().toISOString(),
}));

const initialProfiles: FamilyProfile[] = [
  { id: 'teo', family_id: familyId, display_name: 'Teo', avatar_path: null, is_admin: true, current_location_id: 'spiaggia', last_seen_at: new Date().toISOString(), note_text: 'Ho le chiavi', created_at: new Date().toISOString() },
  { id: 'jessica', family_id: familyId, display_name: 'Jessica', avatar_path: null, is_admin: false, current_location_id: 'spiaggia', last_seen_at: new Date(Date.now() - 8 * 60_000).toISOString(), note_text: '', created_at: new Date().toISOString() },
  { id: 'marco', family_id: familyId, display_name: 'Marco', avatar_path: null, is_admin: false, current_location_id: 'piscina', last_seen_at: new Date(Date.now() - 21 * 60_000).toISOString(), note_text: '', created_at: new Date().toISOString() },
  { id: 'laura', family_id: familyId, display_name: 'Laura', avatar_path: null, is_admin: false, current_location_id: 'area-relax', last_seen_at: new Date(Date.now() - 42 * 60_000).toISOString(), note_text: '', created_at: new Date().toISOString() },
];

export default function DemoPreview() {
  const previewMode = new URLSearchParams(window.location.search);
  const [profiles, setProfiles] = useState(initialProfiles);
  const [locations, setLocations] = useState(initialLocations);
  const [groupMeetup, setGroupMeetup] = useState<GroupMeetup | null>({
    id: 'demo-meetup',
    location_id: 'ristorante',
    location_name: 'Ristorante',
    location_emoji: '🍽️',
    scheduled_for: new Date(Date.now() + 60 * 60_000).toISOString(),
    created_at: new Date().toISOString(),
  });
  const [grandpaCalls, setGrandpaCalls] = useState({ count: 7, date: new Date().toISOString().slice(0, 10) });
  const [showAdmin, setShowAdmin] = useState(previewMode.has('admin'));
  const ownProfile = profiles[0];
  const grouped = useMemo(() => {
    const result = new Map<string, FamilyProfile[]>();
    profiles.forEach((profile) => result.set(profile.current_location_id || '', [...(result.get(profile.current_location_id || '') || []), profile]));
    return result;
  }, [profiles]);

  async function movePerson(profileId: string, locationId: string) {
    setProfiles((current) => current.map((profile) => profile.id === profileId
      ? { ...profile, current_location_id: locationId, last_seen_at: new Date().toISOString() }
      : profile));
  }

  async function addLocation(name: string, emoji: string) {
    setLocations((current) => [...current, {
      id: `custom-${current.length}`,
      family_id: familyId,
      slug: `custom-${current.length}`,
      name,
      emoji,
      kind: 'custom',
      notification_phrase: `in ${name}`,
      sort_order: current.length * 10 + 10,
      is_active: true,
      created_at: new Date().toISOString(),
    }]);
  }

  async function saveNote(noteText: string) {
    setProfiles((current) => current.map((profile) => profile.id === ownProfile.id ? { ...profile, note_text: noteText } : profile));
  }

  async function createMeetup(locationId: string, scheduledFor: string) {
    const location = locations.find((entry) => entry.id === locationId);
    if (!location) return;
    setGroupMeetup({
      id: 'demo-meetup',
      location_id: location.id,
      location_name: location.name,
      location_emoji: location.emoji,
      scheduled_for: scheduledFor,
      created_at: new Date().toISOString(),
    });
  }

  if (previewMode.has('onboarding')) {
    return <Onboarding isSubmitting={false} error={null} onSubmit={async () => undefined} />;
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="brand-lockup">
          <img src="/icons/locus-icon-v2-192.png" width="48" height="48" alt="" />
          <div>
            <p className="eyebrow">Amaika · Calella</p>
            <p className="app-header__name">L.O.C.U.S.</p>
            <p className="app-header__expanded-name">Loved Ones Coordination &amp; Updates System</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="icon-button" type="button" aria-label="Notifiche"><Bell /></button>
          <button className="icon-button icon-button--admin" type="button" onClick={() => setShowAdmin(true)} aria-label="Admin"><Settings2 /></button>
        </div>
      </header>
      <main className="dashboard">
        <section className="dashboard-hero">
          <span className="dashboard-hero__icon"><MapPinned /></span>
          <div><p className="eyebrow">Presenza famiglia</p><h1>Dove siamo adesso?</h1><p>Tocca un luogo per aggiornare la tua posizione.</p></div>
        </section>
        {groupMeetup && <MeetupBanner meetup={groupMeetup} />}
        <GrandpaCallCounter
          grandpaCalls={grandpaCalls}
          isAdmin={ownProfile.is_admin}
          isBusy={false}
          onIncrement={async () => setGrandpaCalls((current) => ({ ...current, count: current.count + 1 }))}
          onSetCount={async (count) => setGrandpaCalls((current) => ({ ...current, count }))}
        />
        <button className="admin-shortcut" type="button" onClick={() => setShowAdmin(true)}><ShieldCheck size={18} /> Modalità admin: puoi spostare tutti</button>
        <section className="places-grid">
          {locations.map((location) => (
            <LocationCard
              key={location.id}
              location={location}
              people={grouped.get(location.id) || []}
              avatarUrls={{}}
              isCurrent={ownProfile.current_location_id === location.id}
              isUpdating={false}
              onChoose={(locationId) => void movePerson(ownProfile.id, locationId)}
            />
          ))}
        </section>
        <FamilyTools
          ownProfile={ownProfile}
          isBusy={false}
          onSaveNote={saveNote}
        />
      </main>
      <footer className="app-footer"><img src="/assets/theo-logo.png" width="26" height="26" alt="" /><span>Powered by T.H.E.O.</span></footer>
      {showAdmin && (
        <AdminSheet
          profiles={profiles}
          locations={locations}
          groupMeetup={groupMeetup}
          isBusy={false}
          onClose={() => setShowAdmin(false)}
          onMovePerson={movePerson}
          onAddLocation={addLocation}
          onCreateMeetup={createMeetup}
          onCancelMeetup={async () => setGroupMeetup(null)}
        />
      )}
    </div>
  );
}
